import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { FileType } from "reedy-data/models";
import { ArrowLeft, LoaderIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useOrganisation } from "~/contexts/organisation-provider";
import { logError } from "~/shared/services/error-reporting";
import { toast } from "~/utils/toast";
import { PaginationController } from "~/components/pagination-controller/pagination-controller";
import LoadingSpinner from "~/components/loading/loading-spinner";
import { FileUploadDialog } from "~/components/file-upload-dialog";
import { Text } from "~/components/text";
import { usePipelineReports } from "~/hooks/use-pipeline-reports";
import { OrganisationOnixUploadFile } from "./components/organisation-onix-upload-file";
import { OrganisationOnixUploadFileReportDialog } from "./components/organisation-onix-upload-file-report-dialog";

export const OrganisationOnixManualUploadPage = () => {
  const [reportId, setReportId] = useState<string | null>(null);
  const [uploading, setUploading] = useState<File | null>(null);
  const { t } = useTranslation(["settings"]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { addFile, id: organisationId } = useOrganisation();

  const { reports, loading, itemsCount, setPage } = usePipelineReports({
    mode: FileType.MANUAL_ONIX_IMPORT,
    organisationId,
  });

  const handleFileUpload = useCallback(
    ([file]: File[]) => {
      setDialogOpen(false);
      setUploading(file);

      setPage(1);

      addFile(file)
        .catch((error) => {
          logError(error.message);
          toast.error(error.message);
        })
        .then((result) => {
          if (result) {
            toast.success(t("settings:organisation.onix.uploading"), {
              icon: (
                <LoaderIcon className="animate-spin duration-2000 w-5 h-5" />
              ),
            });
          }
          setUploading(
            result
              ? new File([file], result.name.replace(/^manualImport\//, ""), {
                  type: file.type,
                })
              : null,
          );
        });
    },
    [addFile, t, setPage],
  );

  useEffect(() => {
    if (
      uploading &&
      reports.some((report) => report.fileName === uploading.name)
    ) {
      // clears the optimistic uploading placeholder once the matching report arrives in the async reports list
      setUploading(null);
    }
  }, [reports, uploading]);

  return (
    <div>
      <div className="flex items-center text-primary-500 mb-6">
        <Link
          to="/settings/organisations/$organisationId/onix-upload"
          params={{ organisationId }}
          className="flex items-center gap-2 text-sm"
        >
          <ArrowLeft className="h-4 w-4 " />
          {t("organisation.onix.back")}
        </Link>
      </div>
      <div className="flex space-between align-center justify-center">
        <div className="grow">
          <Text as="h2" variant="pageHeading" className="mb-2">
            {t("settings:organisation.onix.manual.title")}
          </Text>
          <Text variant="subtle" className="pb-4">
            {t("settings:organisation.onix.manual.description")}
          </Text>
        </div>
        <div>
          <FileUploadDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            onFileUpload={handleFileUpload}
            icon="upload"
            triggerButton={{
              label: t("settings:organisation.onix.fileUpload.button"),
              className: "py-2 font-normal",
            }}
            title={t("settings:organisation.onix.fileUpload.dialog.title")}
            description={t(
              "settings:organisation.onix.fileUpload.dialog.description",
            )}
            accept={{ "text/xml": [".xml", ".onx", ".onix"] }}
            maxSizeMB={25}
          />
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center w-full pt-10">
          <LoadingSpinner />
        </div>
      ) : null}
      <ul className="mt-8 flex flex-col gap-2">
        {uploading ? (
          <OrganisationOnixUploadFile
            key="uploading-file"
            organisationId={organisationId}
            fileName={uploading.name}
            dummy
            fileSizeMB={(uploading.size || 0) / 1024 / 1024}
          />
        ) : null}
        {reports.map((report) => (
          <OrganisationOnixUploadFile
            key={report.id}
            organisationId={organisationId}
            fileName={report.fileName}
            createdAt={new Date(report.createdAt)}
            report={report}
            onActionClick={setReportId}
          />
        ))}
      </ul>
      <div className="mt-4">
        <PaginationController
          itemsCount={itemsCount}
          pageSizeLabel={t("settings:organisation.onix.perPage")}
        />
      </div>
      {reportId && (
        <OrganisationOnixUploadFileReportDialog
          open={!!reportId}
          reportId={reportId}
          onClose={() => setReportId(null)}
        />
      )}
    </div>
  );
};
